import { makeStyles } from '@material-ui/styles'
import ValidatedForm from 'pf9-ui-components/built/components/validatedForm/ValidatedForm'
import { prop } from 'ramda'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { generateApiErrorPayload } from 'api-client/helpers'
import ServiceUnhealthyInfo from 'app/plugins/openstack/components/common/ServiceUnhealthyInfo'
import { sshKeysSelector } from 'app/plugins/openstack/components/security/keys/selectors'
import { type RootState, useAppSelector } from 'app/store'
import { ProgressStatus, StepProgress } from 'core/components/progress/StepProgress'
import StepperModalForm, { type ModalStep } from 'core/elements/modal/StepperModalForm'
import useListAction from 'core/hooks/useListAction'
import useParams from 'core/hooks/useParams'
import useUpdateAction from 'core/hooks/useUpdateAction'
import { type SessionState, sessionStoreKey } from 'core/session/sessionReducers'
import useScopedPreferences from 'core/session/useScopedPreferences'
import type Theme from 'core/themes/model'
import {
  ProgressStep,
  useClusterProgress,
} from 'k8s/components/access-control/hooks/useClusterProgress'
import { createKaapiClusterAddonBody } from 'k8s/components/kaapi/cluster-addons/action-helpers'
import { createKaapiClusterAddon } from 'k8s/components/kaapi/cluster-addons/actions'
import {
  buildEtcdBackupClusterSpec,
  ETCD_BACKUP_ADDON_TYPE,
  ETCD_BACKUP_ADDON_VERSION,
  ETCD_BACKUP_CREATE_FAILED_MESSAGE,
  ETCD_BACKUP_INITIAL_VALUES,
  getEtcdBackupSettingsError,
} from 'k8s/components/kaapi/cluster-addons/etcd-backup'
import { createKaapiClusterClassBody } from 'k8s/components/kaapi/clusters/action-helpers'
import { createKaapiCluster } from 'k8s/components/kaapi/clusters/actions'
import { kaapiClustersSelector } from 'k8s/components/kaapi/clusters/selectors'
import { listResmgrHosts } from 'openstack/components/infrastructure/actions'
import { hasCinderRole } from 'openstack/components/infrastructure/helpers'
import { resmgrHostsSelector } from 'openstack/components/infrastructure/selectors'
import { isNilOrEmpty } from 'utils/fp'
import ClusterArchitectureFieldsOpenStack from './form-components/ClusterArchitectureFieldsOpenStack'
import ClusterConfigFields from './form-components/ClusterConfigFields'
import ClusterNameField from './form-components/ClusterNameField'
import ClusterNetworkingFields from './form-components/ClusterNetworkingFields'
import {
  CNI_INITIAL_VALUES,
  NETWORKING_STEP_DESCRIPTION,
  toCniConfig,
} from './form-components/cniTypes'
import { DEFAULT_VOLUME_SIZE } from './form-components/helpers'
import MasterServerConfigurationFields from './form-components/MasterServerConfigurationFields'
import ServerConfigurationFields, {
  type NodePoolErrors,
} from './form-components/ServerConfigurationFields'
import { CINDER_CSI_VERSION, METALLB_VERSION, waitForTopologyReconciliation } from './helper'

const validateNodePools = (nodePools) => {
  const errors = {}
  nodePools.forEach(({ id, flavor, replicas }) => {
    if (!flavor || !replicas) {
      errors[id] = errors[id] || {}
      if (!flavor) {
        errors[id]['flavor'] = 'A VM flavor selection is required.'
      }
      if (!replicas) {
        errors[id]['replicas'] = 'The value must be greater or equal than 1'
      }
    }
  })
  return errors
}

const initialValues = {
  nodePools: [{ id: 1, volumeSize: DEFAULT_VOLUME_SIZE }],
  masterNodePools: [{ id: 1, volumeSize: DEFAULT_VOLUME_SIZE }],
  ...CNI_INITIAL_VALUES,
  ...ETCD_BACKUP_INITIAL_VALUES,
  enableAddons: true,
  controlPlaneType: 'managed',
  apiServerFlags: '',
  schedulerFlags: '',
  controllerManagerFlags: '',
  metallb: true,
  cinderCsi: true,
  openstackCluster: '',
  image: '',
  sshKey: '',
  gpuAddons: false,
  proxyValue: '',
}

/**
 * @summary - This component is used to deploy a k8s workload cluster for OpenStack infrastructure
 * @flow
 * 1. Cluster Infrastructure:
 *    - User selects Virtualized Cluster from the radio group
 * 2. Cluster Architecture:
 *    - User selects a tenant
 *    - User selects the control plane type and its configuration
 * 3. Node Groups:
 *    - User selects a flavor, enters the number of replicas
 *    - user select storage pool, network and subnet
 * 4. Cluster Configuration:
 *    - User select k8s version
 *    - User adds k8s flags (optional)
 *    - User selects the addons
 * 5. Final Touches -
 *    - Cluster Name: User enters the cluster name
 * 6. User clicks the "Add Cluster" button - handleOpenStackClusterCreation will be called
 *    - validate the form - node groups, image, cluster name
 *    - We have added error handling after each API call
 *    - Create Cluster Class object
 *    For Each Node Group
 *    - Create ClusterAddons object
 * On success, the user will be redirected to the cluster detail page
 */
const ClusterDeploymentForm = ({ onClose }) => {
  const classes = useStyles()
  const { params, getParamsUpdater, updateParams } = useParams<Record<string, any>>(initialValues)
  const { updatePrefs: updateK8sPluginGlobalParams } = useScopedPreferences('k8sPluginGlobalParams')

  const { activeKaapiTenant, activeRegion, domain, activeDomain, activeTenant } = useSelector<
    RootState,
    SessionState
  >(prop(sessionStoreKey))
  const namespace = activeKaapiTenant
  const [nodePoolErrors, setNodePoolErrors] = useState<NodePoolErrors>({})
  const [masterNodePoolErrors, setMasterNodePoolErrors] = useState<NodePoolErrors>({})
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(false)
  const [isGPUFlavorSelected, setIsGPUFlavorSelected] = useState(false)
  const [showProgress, setShowProgress] = useState(false)
  const [isProgressComplete, setIsProgressComplete] = useState(false)
  const {
    progressDetails: clusterClassProgressDetails,
    updateProgress,
    resetProgress,
  } = useClusterProgress({
    initLabel: 'Setting Up Cluster',
  })

  const isCustomControlPlane = useMemo(
    () => params.controlPlaneType === 'custom',
    [params.controlPlaneType],
  )

  // CDFOS-FR-026: Cinder CSI needs a block-storage (persistent-storage) host. When hosts are known
  // and none has the role, force the add-on off and let ClusterConfigFields grey out its toggle.
  // An empty host list (not loaded yet, or not visible to this user) leaves the toggle available.
  useListAction(listResmgrHosts)
  const resmgrHosts = useAppSelector(resmgrHostsSelector)
  const noBlockStorageHost = useMemo(
    () => resmgrHosts.length > 0 && !resmgrHosts.some(hasCinderRole),
    [resmgrHosts],
  )
  useEffect(() => {
    if (noBlockStorageHost && params.cinderCsi !== false) {
      updateParams({ cinderCsi: false })
    }
  }, [noBlockStorageHost, params.cinderCsi, updateParams])

  // Load Clusters
  const clusters = useAppSelector(kaapiClustersSelector)

  // Load ssh keys
  const sshKeys = useSelector(sshKeysSelector)

  // Create Actions
  const {
    update: createCluster,
    updating: creatingCluster,
    error: createClusterError,
  } = useUpdateAction(createKaapiCluster)
  const {
    update: createClusterAddon,
    updating: creatingClusterAddon,
    error: createClusterAddonError,
  } = useUpdateAction(createKaapiClusterAddon)

  const hasError = !!createClusterError || !!createClusterAddonError

  // Error handler helper
  const handleError = useCallback(
    (error: any, message: string, step?: ProgressStep) => {
      setLoading(false)
      setError(error || { title: 'Error', message })
      if (step) {
        updateProgress(step, ProgressStatus.Failed)
      }
    },
    [updateProgress],
  )

  // Set once createOptionalAddons has reported its per-addon failure summary
  const addonFailureReported = useRef(false)

  useEffect(() => {
    if (!hasError || addonFailureReported.current) return
    // Show first error
    const error = createClusterError || createClusterAddonError

    setError({
      title: error?.title || 'Error creating cluster',
      message: error?.message || 'An error occurred while creating the cluster. Please try again.',
    })
  }, [hasError, createClusterError, createClusterAddonError])

  // ---- Validation ----
  const validateClusterParams = (params) => {
    const nodePoolErrors = validateNodePools(params.nodePools)
    setNodePoolErrors(nodePoolErrors)
    const masterNodePoolErrors =
      params.controlPlaneType === 'custom' ? validateNodePools(params.masterNodePools) : {}
    setMasterNodePoolErrors(masterNodePoolErrors)
    if (
      !isNilOrEmpty(nodePoolErrors) ||
      !isNilOrEmpty(masterNodePoolErrors) ||
      !params?.image ||
      !params?.openstackCluster ||
      !params?.sshKey
    ) {
      return false
    }
    return true
  }

  // ---- Infrastructure ----
  const createClusterInfrastructure = async (params, namespace) => {
    // Create Cluster Class Body
    // Params needs to be updated while finalizing the creation flow
    const clusterBody = createKaapiClusterClassBody({
      name: params.clusterName,
      gpuAddons: params.gpuAddons,
      network: params.nodePools[0].network,
      subnet: params.nodePools[0].subnet,
      openstackCluster: params.openstackCluster,
      nodePools: params.nodePools,
      image: params.image,
      region: activeRegion,
      domain: activeDomain?.id || domain?.id,
      tenant: activeTenant,
      controlPlaneType: params.controlPlaneType === 'custom' ? 'colocated' : 'hosted',
      masterNodePool: params.masterNodePools,
      sshKey: sshKeys?.find((key) => key.name === params?.sshKey),
      kubernetesVersion: params.k8sVersion,
      apiServerFlags: params.apiServerFlags ? params.apiServerFlags.split(',') : [],
      schedulerFlags: params.schedulerFlags ? params.schedulerFlags.split(',') : [],
      controllerManagerFlags: params.controllerManagerFlags
        ? params.controllerManagerFlags.split(',')
        : [],
      proxyValue: params.proxyValue,
      // CDFOS-FR-023: CNI selection (immutable after creation)
      cniConfig: toCniConfig(params),
    })
    const { success: cSuccess, error: cError } = await createCluster({
      namespace,
      body: clusterBody,
      clusterName: params.clusterName,
    })
    if (!cSuccess)
      return {
        success: false,
        error: cError,
        message: 'Failed to create Cluster',
      }

    return { success: true }
  }

  // ---- Addons ----
  // CDFOS-FR-025: metallb and cinder-csi ClusterAddons are created when toggled on.
  // CDFOS-FR-027: every addon is attempted even if another fails, and failures are reported per addon.
  const createOptionalAddons = async (params, namespace) => {
    const addons = [
      params.metallb && { addonType: 'metallb', version: METALLB_VERSION },
      params.cinderCsi && { addonType: 'cinder-csi', version: CINDER_CSI_VERSION },
    ].filter(Boolean)
    const results = await Promise.allSettled(
      addons.map(({ addonType, version }) => {
        const body = createKaapiClusterAddonBody({
          clusterName: params.clusterName,
          addonType,
          version,
        })
        return createClusterAddon({ namespace, body })
      }),
    )
    const failures = results.flatMap((result, i) => {
      const { addonType } = addons[i]
      if (result.status === 'rejected') {
        return [{ addonType, reason: result.reason }]
      }
      return result.value?.success ? [] : [{ addonType, reason: result.value?.error }]
    })
    if (failures.length === 0) return { success: true, error: null }
    return {
      success: false,
      error: {
        title: `Failed to create add-on${failures.length > 1 ? 's' : ''}: ${failures
          .map((f) => f.addonType)
          .join(', ')}`,
        message: failures
          .map((f) => `${f.addonType}: ${f.reason?.message || f.reason || 'Unknown error'}`)
          .join('\n'),
      },
    }
  }

  // CDFOS-FR-028: the etcd backup add-on, for a colocated control plane with add-ons on. It
  // decides both whether the add-on is created and whether its settings are validated.
  const isEtcdBackupRequested = (params) =>
    !!params.enableAddons && params.controlPlaneType === 'custom' && !!params.etcdBackup

  const createEtcdBackupAddon = async (params, namespace) => {
    const body = createKaapiClusterAddonBody({
      clusterName: params.clusterName,
      addonType: ETCD_BACKUP_ADDON_TYPE,
      version: ETCD_BACKUP_ADDON_VERSION,
      clusterSpec: buildEtcdBackupClusterSpec(params),
    })
    return createClusterAddon({ namespace, body })
  }

  // Handle OpenStack cluster creation
  const handleOpenStackClusterCreation = async () => {
    addonFailureReported.current = false
    setError(null)
    setLoading(true)
    setShowProgress(true)

    try {
      // Step 0: Validate
      if (!validateClusterParams(params)) {
        setLoading(false)
        setShowProgress(false)
        handleError(null, 'Please fill all the required fields', ProgressStep.Init)
        return
      }
      updateK8sPluginGlobalParams({ showProgressStep: clusters?.length === 0 })
      updateProgress(ProgressStep.Init, ProgressStatus.Success)

      // Step 3: Infra (OpenStackCluster, HCP, Cluster)
      const infraRes = await createClusterInfrastructure(params, namespace)
      if (!infraRes?.success) {
        setLoading(false)
        handleError(infraRes?.error, infraRes?.message, ProgressStep.ControlPlane)
        return
      }

      // Step 4: Optional addons
      if (params.metallb || params.cinderCsi) {
        const { success, error } = await createOptionalAddons(params, namespace)
        if (!success) {
          addonFailureReported.current = true
          handleError(error, 'Failed to create ClusterAddon', ProgressStep.ControlPlane)
          return
        }
      }
      if (isEtcdBackupRequested(params)) {
        const { success, error } = await createEtcdBackupAddon(params, namespace)
        if (!success) {
          handleError(error, ETCD_BACKUP_CREATE_FAILED_MESSAGE, ProgressStep.ControlPlane)
          return
        }
      }
      updateProgress(ProgressStep.ControlPlane, ProgressStatus.Success)

      // Step 5: Wait for topology reconciliation and redirect
      if (!hasError) {
        try {
          await waitForTopologyReconciliation({
            clusterName: params.clusterName,
          })
          updateProgress(ProgressStep.NodeGroups, ProgressStatus.Success)
          setIsProgressComplete(true)
        } catch (err) {
          handleError(err, 'Failed to redirect to cluster list page', ProgressStep.NodeGroups)
          return
        }
      }
    } finally {
      setLoading(false)
    }
  }

  const onCloseProgressScreen = useCallback(() => {
    setError(null)
    setShowProgress(false)
    updateK8sPluginGlobalParams({ showProgressStep: false })
    setIsProgressComplete(false)
    resetProgress()
  }, [resetProgress])

  // Reset form callback
  const handleReset = useCallback(() => {
    setError(null)
    setLoading(false)
    setIsProgressComplete(false)
    setShowProgress(false)
    updateK8sPluginGlobalParams({ showProgressStep: false })
    resetProgress()
    onClose()
  }, [resetProgress, onClose])

  const submitting = creatingCluster || creatingClusterAddon || loading

  const steps: ModalStep[] = [
    {
      stepId: 1,
      headerTitle: 'Cluster Architecture',
      description:
        'Select the compute infrastructure for this cluster. Using Virtualized Clusters enables integrated management of node groups, cluster autoscaler, storage and load balancers.',
      component: (
        <div className={classes.formSection}>
          <ClusterNameField
            params={params}
            getParamsUpdater={getParamsUpdater}
            clusters={clusters}
          />
          <ClusterArchitectureFieldsOpenStack params={params} getParamsUpdater={getParamsUpdater} />
        </div>
      ),
    },
    ...(isCustomControlPlane
      ? [
          {
            stepId: 2,
            headerTitle: 'Control Plane Node Configuration',
            description:
              'Configure the compute resources for control plane nodes. These nodes host the Kubernetes control plane components.',
            component: (
              <div className={classes.formSection}>
                <MasterServerConfigurationFields
                  id="masterNodePools"
                  params={params}
                  getParamsUpdater={getParamsUpdater}
                  nodePoolErrors={masterNodePoolErrors}
                />
              </div>
            ),
            stepValidator: () => {
              const nodePoolErrors = validateNodePools(params.masterNodePools)
              setMasterNodePoolErrors(nodePoolErrors)
              if (!isNilOrEmpty(nodePoolErrors)) {
                return {
                  success: false,
                }
              }
              return { success: true }
            },
          },
        ]
      : []),
    {
      stepId: isCustomControlPlane ? 3 : 2,
      headerTitle: 'Servers, Storage and Network',
      description:
        'Configure the compute resources for this cluster. Using Virtualized Clusters enables integrated management of node groups, cluster autoscaler, storage and load balancers.',
      component: (
        <div className={classes.formSection}>
          <ServerConfigurationFields
            id="nodePools"
            params={params}
            getParamsUpdater={getParamsUpdater}
            nodePoolErrors={nodePoolErrors}
            setIsGPUFlavorSelected={setIsGPUFlavorSelected}
          />
        </div>
      ),
      stepValidator: () => {
        const nodePoolErrors = validateNodePools(params.nodePools)
        setNodePoolErrors(nodePoolErrors)
        if (!isNilOrEmpty(nodePoolErrors)) {
          return {
            success: false,
          }
        }
        return { success: true }
      },
    },
    // CDFOS-FR-024: Networking step (CNI, kube-proxy mode, proxy)
    {
      stepId: isCustomControlPlane ? 4 : 3,
      headerTitle: 'Networking',
      description: NETWORKING_STEP_DESCRIPTION,
      component: (
        <div className={classes.formSection}>
          <ClusterNetworkingFields params={params} getParamsUpdater={getParamsUpdater} />
        </div>
      ),
    },
    {
      stepId: isCustomControlPlane ? 5 : 4,
      headerTitle: 'Configure your Cluster',
      description:
        'Configure your cluster’s Kubernetes version, your GPUs partitioning strategy (if applicable), optional Kubernetes flags, and add-ons.',
      component: (
        <div className={classes.formSection}>
          <ClusterConfigFields
            params={params}
            getParamsUpdater={getParamsUpdater}
            isOpenStackCluster
            isGPUFlavorSelected={isGPUFlavorSelected}
            cinderCsiUnavailable={noBlockStorageHost}
          />
        </div>
      ),
      stepValidator: () => {
        if (!params?.image || !params?.k8sVersion) {
          return {
            success: false,
          }
        }
        // CDFOS-FR-029: an etcd backup needs at least one backup kind
        const etcdBackupError = isEtcdBackupRequested(params)
          ? getEtcdBackupSettingsError(params)
          : null
        if (etcdBackupError) {
          return { success: false, error: new Error(etcdBackupError) }
        }
        return { success: true }
      },
    },
  ]

  return (
    <StepperModalForm
      steps={steps}
      title="Deploy A New Cluster"
      maxWidth={876}
      open={true}
      onClose={handleReset}
      onSubmit={showProgress && isProgressComplete ? handleReset : handleOpenStackClusterCreation}
      error={error}
      resetError={() => setError(null)}
      submitting={submitting}
      ValidatedFormComponent={ValidatedForm as any}
      params={params}
      customWarningComponent={
        <ServiceUnhealthyInfo
          action="Cluster Creation"
          hypervisor
          imageLibrary
          blockStorage
          network
        />
      }
      closeProgressScreen={onCloseProgressScreen}
      isProgressComplete={isProgressComplete}
      showProgress={showProgress}
      resetProgress={resetProgress}
      customStepProgressComponent={
        <StepProgress
          progressDetails={clusterClassProgressDetails}
          title={`Cluster deployment is in progress. 
              Please wait and do not close this dialog, or the deployment will be cancelled.`}
          errorMessage={error?.message || generateApiErrorPayload(error)}
        />
      }
    />
  )
}

const useStyles = makeStyles<Theme>((theme) => ({
  modal: {
    '& .modal-body': {
      padding: '32px 24px 24px 32px',
    },
    '& .modal-body .progress-root .progressContent > form': {
      marginBottom: 0,
    },
  },
  formSection: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gridGap: theme.spacing(2),
  },
}))

export default ClusterDeploymentForm
